/**
 * Affected-road GPS detection hook
 *
 * Mirrors the basic-info `RoadDisplayField` GPS flow, but sources coordinates
 * from the user's REAL device position (`navigator.geolocation` via
 * `geolocationService`) instead of the obstruction's map pin. On explicit
 * request it acquires the current GPS fix, runs reverse-geocoding through the
 * shared `useGPSLocationSuggestion`, maps the result to a road name, and writes
 * it into the affected-road draft. Detection runs ONLY on request — there is no
 * auto-detection on mount or anchor change.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGPSLocationSuggestion } from '@/hooks/useGPSLocationSuggestion';
import { useRoadMapping } from '@/hooks/useRoadMapping';
import { geolocationService } from '@/services/geolocation/geolocationService';
import { getGeolocationErrorMessage } from '@/utils/geolocation/errorMessages';
import type { RoadSource } from '@/components/common/RoadDisplayField';

interface UseAffectedRoadGpsDetectionProps {
  /** Writes the resolved/typed road value into the draft field. */
  setLocation: (value: string) => void;
}

interface UseAffectedRoadGpsDetectionReturn {
  roadSource: RoadSource;
  isDetectingGPS: boolean;
  gpsError: string | null;
  handleRequestGPSDetection: () => void;
  handleManualRoadInput: (value: string) => void;
}

export function useAffectedRoadGpsDetection({
  setLocation,
}: UseAffectedRoadGpsDetectionProps): UseAffectedRoadGpsDetectionReturn {
  const { t } = useTranslation();
  const { mapGPSToRoad } = useRoadMapping(t);
  const { detectLocation, suggestion, clearSuggestion, isDetecting, error } =
    useGPSLocationSuggestion();

  const [roadSource, setRoadSource] = useState<RoadSource>(null);
  const [isAcquiringPosition, setIsAcquiringPosition] = useState(false);
  const [positionError, setPositionError] = useState<string | null>(null);

  // Apply the resolved GPS suggestion to the draft field once it arrives.
  useEffect(() => {
    if (!suggestion) return;

    const mappedRoad = mapGPSToRoad(suggestion.suggestion);
    if (mappedRoad) {
      setLocation(mappedRoad);
      setRoadSource('gps');
    }

    clearSuggestion();
  }, [suggestion, mapGPSToRoad, setLocation, clearSuggestion]);

  const handleManualRoadInput = useCallback(
    (value: string) => {
      setRoadSource(value ? 'manual' : null);
      setPositionError(null);
      setLocation(value);
    },
    [setLocation]
  );

  const handleRequestGPSDetection = useCallback(() => {
    const detect = async (): Promise<void> => {
      setPositionError(null);
      setIsAcquiringPosition(true);

      const result = await geolocationService.getCurrentPosition();

      setIsAcquiringPosition(false);

      if (!result.success || !result.coordinates) {
        setPositionError(
          getGeolocationErrorMessage(
            result.errorCode ?? 'POSITION_UNAVAILABLE',
            t
          )
        );
        return;
      }

      await detectLocation(result.coordinates);
    };

    void detect();
  }, [detectLocation, t]);

  return {
    roadSource,
    isDetectingGPS: isAcquiringPosition || isDetecting,
    gpsError: positionError ?? error,
    handleRequestGPSDetection,
    handleManualRoadInput,
  };
}
